import '../styles/LeadsTable.css';
import React, { useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

function CrawlArrow() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 8h10M9 4l4 4-4 4" />
    </svg>
  );
}

function RetryMark() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9" />
      <path d="M13.5 2v3.5H10" />
    </svg>
  );
}

function EnvelopeMark() {
  return (
    <svg viewBox="0 0 48 36" fill="none" aria-hidden="true">
      <rect x="1.5" y="1.5" width="45" height="33" rx="3" stroke="currentColor" strokeWidth="1.1" />
      <path d="M2.25 3.25 L24 20.5 L45.75 3.25" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.5 33 C13.5 24.5 34.5 24.5 45.5 33" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" opacity="0.5" />
    </svg>
  );
}

function splitSource(url) {
  const raw = typeof url === 'string' ? url : '';
  const clean = raw.replace(/^https?:\/\//, '');
  const slash = clean.indexOf('/');
  if (slash === -1) return { host: clean || '—', path: '' };
  return { host: clean.slice(0, slash), path: clean.slice(slash) };
}

function statusLabel(status) {
  const map = { queued: 'Queued', running: 'Running', completed: 'Completed', failed: 'Failed' };
  return map[status] || 'Unknown';
}

function crawlHref(id) {
  return `/crawls?id=${encodeURIComponent(id)}`;
}

function LeadsTable() {
  const leadsState = useProjectCollection('leads');
  const crawlsState = useProjectCollection('crawls');
  const [crawlFilter, setCrawlFilter] = useState('all');
  const [retrying, setRetrying] = useState(false);

  const leads = Array.isArray(leadsState.data) ? leadsState.data : [];
  const crawls = Array.isArray(crawlsState.data) ? crawlsState.data : [];
  const crawlById = crawls.reduce((acc, crawl) => {
    acc[crawl.id] = crawl;
    return acc;
  }, {});

  const loading = leadsState.loading || crawlsState.loading;
  const hasError = Boolean(leadsState.error || crawlsState.error);
  const errorMessage = leadsState.error || crawlsState.error;
  const filterActive = crawlFilter !== 'all';
  const visibleLeads = filterActive ? leads.filter((lead) => lead.crawlId === crawlFilter) : leads;

  const filterOptions = [];
  const seen = new Set();
  crawls.forEach((crawl) => {
    if (crawl && crawl.id && !seen.has(crawl.id)) {
      seen.add(crawl.id);
      filterOptions.push(crawl);
    }
  });
  leads.forEach((lead) => {
    if (lead && lead.crawlId && !seen.has(lead.crawlId) && !crawlById[lead.crawlId]) {
      seen.add(lead.crawlId);
      filterOptions.push({ id: lead.crawlId, target: lead.crawlId });
    }
  });

  function handleRetry() {
    setRetrying(true);
    try {
      leadsState.refresh();
      crawlsState.refresh();
    } finally {
      setRetrying(false);
    }
  }

  function crawlCell(lead) {
    const crawl = crawlById[lead.crawlId];
    return (
      <div className="lt-crawl">
        <a className="lt-crawl-link" href={crawlHref(lead.crawlId)}>
          {lead.crawlId}
          <CrawlArrow />
        </a>
        {crawl ? (
          <span className={`lt-status lt-status--${crawl.status}`}>{statusLabel(crawl.status)}</span>
        ) : null}
      </div>
    );
  }

  function sourceCell(lead) {
    const parts = splitSource(lead.sourcePage);
    return (
      <span className="lt-source">
        <span className="lt-source-host">{parts.host}</span>
        {parts.path ? <span className="lt-source-path">{parts.path}</span> : null}
      </span>
    );
  }

  const countText = filterActive
    ? `${visibleLeads.length} of ${leads.length}`
    : String(leads.length);
  const noun = (filterActive ? visibleLeads.length : leads.length) === 1 ? 'lead' : 'leads';

  return (
    <section className="lt-section" aria-labelledby="lt-heading">
      <div className="lt-rail">
        <div className="lt-panel">
          <h2 id="lt-heading" className="lt-panel-title">Extracted email leads</h2>

          <div className="lt-toolbar">
            <div className="lt-filter">
              <label className="lt-filter-label" htmlFor="lt-crawl-filter">Filter by crawl</label>
              <div className="lt-filter-row">
                <div className="lt-select-wrap">
                  <select
                    id="lt-crawl-filter"
                    className="lt-select"
                    value={crawlFilter}
                    onChange={(event) => setCrawlFilter(event.target.value)}
                    aria-describedby="lt-count"
                  >
                    <option value="all">All crawls</option>
                    {filterOptions.map((crawl) => (
                      <option key={crawl.id} value={crawl.id}>
                        {crawl.target || crawl.id}{crawl.target ? ` — ${crawl.id}` : ''}
                      </option>
                    ))}
                  </select>
                  <svg className="lt-select-caret" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M2.5 4.5 6 8l3.5-3.5" />
                  </svg>
                </div>
                {filterActive ? (
                  <button type="button" className="lt-clear" onClick={() => setCrawlFilter('all')}>
                    Clear filter
                  </button>
                ) : null}
              </div>
            </div>
            <p className="lt-count" id="lt-count" aria-live="polite">
              <strong>{countText}</strong> {noun}
            </p>
          </div>

          {hasError ? (
            <div className="lt-error" role="alert">
              <p className="lt-error-text">
                <strong>Leads could not be loaded.</strong>{' '}
                {typeof errorMessage === 'string' && errorMessage ? errorMessage : 'The request did not complete.'}
              </p>
              <button type="button" className="lt-retry" onClick={handleRetry} disabled={retrying}>
                <RetryMark />
                {retrying ? 'Retrying…' : 'Retry'}
              </button>
            </div>
          ) : loading ? (
            <div className="lt-skeleton" role="status" aria-busy="true" aria-label="Loading extracted leads">
              {['a', 'b', 'c', 'd'].map((row) => (
                <div className="lt-skel-row" key={row} aria-hidden="true">
                  <span className="lt-skel-cell" />
                  <span className="lt-skel-cell lt-skel-cell--mid" />
                  <span className="lt-skel-cell lt-skel-cell--short" />
                </div>
              ))}
            </div>
          ) : visibleLeads.length === 0 ? (
            <div className="lt-state">
              <span className="lt-state-mark"><EnvelopeMark /></span>
              <h3 className="lt-state-title">
                {filterActive ? 'No leads from this crawl yet' : 'No leads extracted yet'}
              </h3>
              <p className="lt-state-text">
                {filterActive
                  ? 'This crawl has not produced any email leads. Clear the filter to see every lead, or start another crawl.'
                  : 'When a crawl finds email addresses, they appear here with the page they were found on and the crawl they belong to.'}
              </p>
              {filterActive ? (
                <button type="button" className="lt-clear" onClick={() => setCrawlFilter('all')}>
                  Show all leads
                </button>
              ) : (
                <a className="lt-state-link" href="/new-crawl">
                  Start a crawl
                  <CrawlArrow />
                </a>
              )}
            </div>
          ) : (
            <>
              <div className="lt-table-wrap">
                <table className="lt-table">
                  <colgroup>
                    <col className="lt-col-email" />
                    <col className="lt-col-source" />
                    <col className="lt-col-crawl" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th scope="col">Email</th>
                      <th scope="col">Source page</th>
                      <th scope="col">Crawl</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleLeads.map((lead) => (
                      <tr key={lead.id}>
                        <td><span className="lt-email">{lead.email}</span></td>
                        <td>{sourceCell(lead)}</td>
                        <td>{crawlCell(lead)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="lt-cards">
                {visibleLeads.map((lead) => {
                  const parts = splitSource(lead.sourcePage);
                  const crawl = crawlById[lead.crawlId];
                  return (
                    <li className="lt-card" key={lead.id}>
                      <p className="lt-card-email">{lead.email}</p>
                      <div className="lt-card-field">
                        <span className="lt-card-label">Source page</span>
                        <span className="lt-card-value lt-card-value--body">{parts.host}{parts.path}</span>
                      </div>
                      <div className="lt-card-foot">
                        <div className="lt-card-host">
                          <span className="lt-card-label">Crawl</span>
                          <a className="lt-crawl-link" href={crawlHref(lead.crawlId)}>
                            {lead.crawlId}
                            <CrawlArrow />
                          </a>
                        </div>
                        {crawl ? (
                          <span className={`lt-status lt-status--${crawl.status}`}>{statusLabel(crawl.status)}</span>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default LeadsTable;